import { useEffect, useState } from "react";

/**
 * <ClientOnly> — renders children only after the browser has fully hydrated.
 *
 * Why: some third-party components (e.g. react-fast-marquee) delay their own
 * render until a useState/useEffect cycle completes. During prerender in
 * Puppeteer we let those effects run and capture the resulting HTML — but on
 * hydration React expects the initial render to return the same HTML, and
 * these libraries render `null` initially → mismatch.
 *
 * ClientOnly resolves this by:
 *   - Prerender: sees `window.__PRERENDER__ === true`, renders `null` (which
 *     matches what React will render on hydration's initial pass).
 *   - Hydration initial pass: `mounted === false`, renders `null`. Matches
 *     prerendered HTML → no mismatch warning.
 *   - Post-hydration: `useEffect` flips `mounted = true`, children render.
 */
export default function ClientOnly({ children, fallback = null }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // Skip flipping mounted while Puppeteer is capturing the snapshot;
    // Puppeteer clears the flag right before it grabs outerHTML.
    if (typeof window !== "undefined" && window.__PRERENDER__ === true) return;
    setMounted(true);
  }, []);
  return mounted ? children : fallback;
}
